import { HttpClient } from '@angular/common/http';
import { Injectable, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { environment } from '@environment';
import {
  UserInfo,
  ConfigValue,
  WheelTheme,
  Wheel,
  UserAction,
} from '@longnecktech/splash-commons-fe';
import { UserActivityService } from '@services/user-activity.service';
import {
  BehaviorSubject,
  map,
  interval,
  filter,
  switchMap,
  Observable,
  mergeMap,
  catchError,
} from 'rxjs';
import { UserActionService } from './user-action.service';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private http = inject(HttpClient);
  private userActivityService = inject(UserActivityService);
  private router = inject(Router);
  private userActionService = inject(UserActionService);

  private _isMobile = new BehaviorSubject<boolean>(false);
  private _user = new BehaviorSubject<UserInfo | undefined>(undefined);
  private _game = new BehaviorSubject<Wheel | undefined>(undefined);
  private _theme = new BehaviorSubject<WheelTheme | undefined>(undefined);
  private _infoContent = new BehaviorSubject<string | undefined>(undefined);
  private _isMusicMuted = new BehaviorSubject<boolean>(false);

  thirdPartyToken = '';
  gameUuid?: string;
  hubGameUuid?: string;
  instance?: string;
  isFromHub = false;
  readonly user$ = this._user.asObservable();
  readonly isMobile$ = this._isMobile.asObservable();
  readonly game$ = this._game.asObservable();
  readonly prizes$ = this.game$.pipe(
    map((game) => (game?.segments || []).filter((seg) => !!seg.assetLabel)),
  );
  readonly infoContent$ = this._infoContent.asObservable();
  readonly theme$ = this._theme.asObservable();
  isAnySoundAssetInTheme$ = this.theme$.pipe(
    map((theme) =>
      theme
        ? theme.lockingSymbolSoundAsset ||
          theme.resultNoPrizeSoundAsset ||
          theme.resultPrizeRevealSoundAsset ||
          theme.spinSoundAsset
        : false,
    ),
  );
  readonly thereAnimation$ = this.theme$.pipe(
    map((theme) => theme?.backgroundAnimation),
  );
  readonly isMusicMuted$ = this._isMusicMuted.asObservable();
  private destroyRef = inject(DestroyRef);

  setUser(userInfo: UserInfo): void {
    this._user.next(userInfo);
  }

  setIsMobile(isMobile: boolean): void {
    this._isMobile.next(isMobile);
  }

  setCurrentGame(game: Wheel): void {
    this._theme.next(game.theme);
    this._game.next(game);
    this.gameUuid = game.uuid;
  }

  getThemeLabels(): ConfigValue[] | undefined {
    return this._theme.value?.labels;
  }

  setInfoContent(type: string | undefined): void {
    this._infoContent.next(type);
  }

  getUserLanguage(): string | undefined {
    return this._user.value?.language;
  }

  getCurrentGame(): Wheel | undefined {
    return this._game.value;
  }

  toggleMuted(isMuted?: boolean): void {
    this._isMusicMuted.next(isMuted ?? !this._isMusicMuted.value);
  }

  updateAuthToken(): void {
    // Run every 10 minutes
    interval(10 * 60 * 1000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // this interval must not refresh the token trigger if the user is not active
        filter(() => !this.userActivityService.isUserInactive()),
        switchMap(() => this.fetchToken()),
      )
      .subscribe({
        next: (user) => {
          this.setUser(user);
        },
        error: () => {
          this.router.navigate(['/error'], { queryParamsHandling: 'preserve' });
        },
      });
  }

  fetchToken(): Observable<UserInfo> {
    return this.http
      .post<UserInfo>(`${environment.backendUrl}/api/public/auth/token`, {
        token: this.thirdPartyToken,
        instance: this.instance,
      })
      .pipe(
        mergeMap((user) =>
          this.userActionService
            .sendAction(UserAction.GAME_LOGIN, {}, this._game.value?.uuid || '')
            .pipe(map(() => user)),
        ),
        catchError((err) => {
          console.error('Error fetching token:', err);
          throw err;
        }),
      );
  }
}
